import { computed, ref, type Ref } from 'vue'
import { useCoreStore, useVisitorsStore } from '@/shared/store'
import { usePrepareInboundAddon } from '@/shared/composables/usePrepareInboundAddon'
import { INBOUND_ADDON_CODE, INBOUND_ADDON_KEYS } from '@/shared/constants/other.constants'
import { inboundCheckResponse } from '@/shared/constants/validationMessages.constants'
import type { VisitorType } from '@/shared/types/store.types'
import { isYoungerThan23 } from '../QuoteWidget.helpers'

export interface UseInboundAddonFlowDeps {
  onToggle: (isOn: boolean, inboundPrice: number) => void
  onView: () => void
}

export const useInboundAddonFlow = (deps: UseInboundAddonFlowDeps) => {
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const { isInboundAddonEnabled, checkPrepareInboundAddon } = usePrepareInboundAddon()

  const manualSelected = computed(() => visitorsStore.manualSelectedInboundAddon)

  const isAbTest = INBOUND_ADDON_KEYS.includes(coreStore.getAbValue(INBOUND_ADDON_CODE))
  const isEnabled = isInboundAddonEnabled

  const isSelected = ref<boolean>(manualSelected.value ?? false)
  const totalPriceWithAddon: Ref<number | null> = ref(null)
  const showAddon = ref(false)
  const inboundPolicyExists = ref(false)
  const isLoading = ref(false)

  const isShown = computed(() => isAbTest && showAddon.value && isEnabled)

  const shouldAutoDeselect = () =>
    (manualSelected.value !== null && !manualSelected.value) ||
    (isYoungerThan23(visitorsStore.holder.birthDate ?? '') && manualSelected.value === null)

  const onSelectToggle = async (): Promise<void> => {
    isLoading.value = true
    isSelected.value = !isSelected.value
    visitorsStore.setManualSelectedInboundAddon(isSelected.value)

    if (isSelected.value) {
      await visitorsStore
        .updateQuote('addon')
        ?.then((res) => {
          totalPriceWithAddon.value = res?.data?.totalPrice ?? null
        })
        .catch(async () => {
          totalPriceWithAddon.value = null
          isSelected.value = false
          await visitorsStore.updateQuote()
        })
    } else {
      await visitorsStore.updateQuote()
      totalPriceWithAddon.value = null
    }

    deps.onToggle(isSelected.value, visitorsStore.inboundPrice || 0)
    isLoading.value = false
  }

  const getQuoteForActions = async (): Promise<void> => {
    if (!isAbTest || !isEnabled) return

    await visitorsStore
      .getInboundAddonQuote()
      .then(async () => {
        if (
          visitorsStore.inboundPrice &&
          visitorsStore.inboundPrice > 0 &&
          visitorsStore.inboundQuoteNumber
        ) {
          showAddon.value = true
          deps.onView()

          if (shouldAutoDeselect()) {
            totalPriceWithAddon.value = null
            isSelected.value = false
            await visitorsStore.updateQuote()
            return
          }

          await visitorsStore
            .updateQuote('addon')
            ?.then((res) => {
              totalPriceWithAddon.value = res?.data?.totalPrice ?? null
              isSelected.value = !!totalPriceWithAddon.value
            })
            .catch(() => {
              totalPriceWithAddon.value = null
              isSelected.value = false
              showAddon.value = false
            })
        }
      })
      .catch(() => {
        totalPriceWithAddon.value = null
        isSelected.value = false
        showAddon.value = false
      })
  }

  const checkForHolder = async (
    holder: VisitorType
  ): Promise<{ ok: boolean; error?: unknown }> => {
    if (!checkPrepareInboundAddon(holder, visitorsStore.entryValue)) {
      visitorsStore.setInboundAddon(false)
      showAddon.value = false
      totalPriceWithAddon.value = null
      return { ok: true }
    }

    try {
      const res = await visitorsStore.inboundAddonCheck(
        holder.passport,
        visitorsStore.getVisitDates.startDate,
        visitorsStore.getVisitDates.tillDate
      )
      visitorsStore.setInboundAddon(res?.success || false)
      if (!visitorsStore.isInboundAddon) {
        showAddon.value = false
        totalPriceWithAddon.value = null
        return { ok: true }
      }
      await getQuoteForActions()
      return { ok: true }
    } catch (error) {
      const errorCode = (error as { response?: { data?: { errorCode?: string } } })?.response?.data
        ?.errorCode
      inboundPolicyExists.value = errorCode === inboundCheckResponse
      return { ok: false, error }
    }
  }

  const validateBeforePayment = async (
    holder: VisitorType
  ): Promise<{ ok: boolean; error?: unknown }> => {
    if (!isSelected.value) return { ok: true }

    try {
      await visitorsStore.inboundAddonCheck(
        holder.passport,
        visitorsStore.getVisitDates.startDate,
        visitorsStore.getVisitDates.tillDate
      )
      return { ok: true }
    } catch (error) {
      const errorCode = (error as { response?: { data?: { errorCode?: string } } })?.response?.data
        ?.errorCode
      inboundPolicyExists.value = errorCode === inboundCheckResponse
      if (inboundPolicyExists.value) {
        isSelected.value = false
        isLoading.value = true
        visitorsStore.setManualSelectedInboundAddon(false)
        await visitorsStore.updateQuote().catch(() => {})
        totalPriceWithAddon.value = null
        isLoading.value = false
        deps.onToggle(false, visitorsStore.inboundPrice || 0)
        return { ok: false }
      }
      return { ok: false, error }
    }
  }

  const restoreFromInitialState = async (): Promise<void> => {
    if (!visitorsStore.isInboundAddon || !isEnabled) {
      totalPriceWithAddon.value = null
      isSelected.value = false
      return
    }

    await visitorsStore
      .getInboundAddonQuote()
      .then(async () => {
        if (
          visitorsStore.inboundPrice &&
          visitorsStore.inboundPrice > 0 &&
          visitorsStore.inboundQuoteNumber
        ) {
          showAddon.value = true
          deps.onView()

          if (shouldAutoDeselect()) {
            totalPriceWithAddon.value = null
            isSelected.value = false
            return
          }

          await visitorsStore
            .updateQuote('addon')
            ?.then((res) => {
              totalPriceWithAddon.value = res?.data?.totalPrice ?? null
              isSelected.value = !!totalPriceWithAddon.value
            })
            .catch(() => {
              totalPriceWithAddon.value = null
              isSelected.value = false
            })
        }
      })
      .catch(() => {
        showAddon.value = false
        totalPriceWithAddon.value = null
      })
  }

  return {
    isSelected,
    totalPriceWithAddon,
    inboundPolicyExists,
    isLoading,
    isShown,
    isAbTest,
    onSelectToggle,
    getQuoteForActions,
    checkForHolder,
    validateBeforePayment,
    restoreFromInitialState,
  }
}
